<template>
  <v-row class="intervener-transaction-card" :test-id="testId" dense justify="start" align="center">
    <v-col class="text-center pa-0" cols="2" md="1" style="border-right: 1px solid var(--card-border)">
      <v-icon test-id="icon-intervener-card" :color="getIntervenerColor(index)" size="42">person_outline</v-icon>
    </v-col>

    <v-col cols="6" md="3" class="d-none d-md-block" align-self="center">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title test-id="reference-label-intervener-card" class="font-weight-medium">
            {{ $vuetify.lang.t("$vuetify.reference") }}
          </v-list-item-title>
          <v-list-item-subtitle test-id="reference-intervener-card" class="col-intervener-text">
            {{ reference }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-col>

    <v-col cols="6" md="3">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title test-id="name-label-intervener-card" class="font-weight-medium">
            {{ $vuetify.lang.t("$vuetify.contact_complete_name") }}
          </v-list-item-title>
          <v-list-item-subtitle test-id="name-intervener-card" class="col-intervener-text">
            {{ name }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-col>

    <v-col class="d-none d-md-block">
      <v-list-item>
        <v-list-item-content style="max-width: 150px;">
          <v-list-item-title v-if="contact" test-id="contact-label-intervener-card" class="font-weight-medium">
            {{ $vuetify.lang.t("$vuetify.contact") }}
          </v-list-item-title>

          <v-list-item-subtitle test-id="contact-intervener-card" class="col-intervener-text">
            <span class="text-ellipsis"> {{ contact }}</span>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-col>

    <!-- Con el QR inline la columna de acciones queda vacía: el acceso a la firma lo da el
         botón "Abrir en este dispositivo" del propio panel -->
    <v-col v-if="signCeremony" class="hidden-md-and-down">
      <v-row v-if="!showInlineQr" class="pr-md-4" dense justify="center" justify-md="end" align="center">
        <v-btn
          v-if="signCeremonyEnabled && intervenerHasURL"
          color="var(--primary-alternative)"
          small
          style="color: var(--clear)"
          target="_blank"
          @click="handleSignatureClick"
        >
          <v-icon class="mr-2" size="20">mdi-draw-pen</v-icon>
          <span>{{ $vuetify.lang.t("$vuetify.goToSignature") }}</span>
        </v-btn>

        <span v-else-if="intervenerHasURL" class="cts-color-tertiary cts-font-size-4">
          <v-icon small>check</v-icon>
          {{ $vuetify.lang.t("$vuetify.signed") }}
        </span>

        <!-- Sin ceremonia creada aún no se sabe si el firmante tendrá acciones -->
        <span v-else-if="!transactionCreated" class="cts-color-tertiary cts-font-size-4"
              test-id="intervener-card-in-progress">
          {{ $vuetify.lang.t("$vuetify.inProgress") }}
        </span>

        <span v-else class="cts-color-tertiary cts-font-size-4" test-id="intervener-card-without-actions">
          {{ $vuetify.lang.t("$vuetify.withoutActions") }}
        </span>
      </v-row>
    </v-col>

    <!-- mobile -->
    <v-col v-if="signCeremony && !showInlineQr" class="hidden-md-and-up">
      <v-btn
        v-if="signCeremonyEnabled && intervenerHasURL"
        color="var(--primary-alternative)"
        small
        style="color: var(--clear)"
        target="_blank"
        class="py-5 px-1"
        @click="handleSignatureClick"
      >
        <div class="d-flex flex-column">
          <v-icon class="mr-1" size="16">mdi-draw-pen</v-icon>
          <span>{{ $vuetify.lang.t("$vuetify.goToSignature") }}</span>
        </div>
      </v-btn>
    </v-col>

    <v-dialog v-if="confirmDialog" v-model="confirmDialog" width="600">
      <v-card class="pa-8 pb-4">
        <v-card-text>
          <v-row class="mb-4" dense justify="center">
            <span class="cts-font-size-title font-weight-medium">{{ intervener.personal_info.name }}</span>
          </v-row>
          <p class="cts-font-size-2">
            {{ $vuetify.lang.t("$vuetify.intervenerStartCeremony") }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-row dense justify="center">
            <v-btn class="mr-2" color="var(--primary)" outlined @click="confirmDialog = false">
              {{ $vuetify.lang.t("$vuetify.cancel") }}
            </v-btn>

            <v-btn color="var(--primary-alternative)" dark @click="openFrontRenderer()">
              {{ $vuetify.lang.t("$vuetify.goToSignature") }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Firmante único: QR e instrucciones en la segunda fila de la tarjeta, sin diálogo -->
    <v-col v-if="showInlineQr" cols="12" class="px-0 pt-4 pb-4 intervener-card-qr"
           test-id="inline-qr-intervener-card">
      <IntervenerSignatureQrPanel
        :signature-url="signatureUrl"
        :size="180"
        inline
        @openFrontRenderer="openFrontRenderer()"
      />
    </v-col>

    <!-- Dos o más firmantes: se mantiene el QR dentro del diálogo -->
    <v-dialog v-if="qrDialog" v-model="qrDialog" width="560">
      <IntervenerSignatureQrPanel
        :name="intervener.personal_info.name"
        :signature-url="signatureUrl"
        closable
        @openFrontRenderer="openFrontRenderer()"
        @close="qrDialog = false"
      />
    </v-dialog>
  </v-row>
</template>

<script>
import { getIntervenerColor } from "@/plugins/helpers";
import IntervenerSignatureQrPanel from "@/components/sections/transactions/components/IntervenerSignatureQrPanel.vue";

export default {
  name: "IntervenerCard",
  components: { IntervenerSignatureQrPanel },
  props: {
    intervener: Object,
    index: Number,
    type: String,
    transactionFinish: Boolean,
    signCeremony: Boolean,
    signCeremonyEnabled: Boolean,
    faceToFaceQrLink: Boolean,
    faceToFaceHideConfirm: Boolean,
    // True cuando la transacción tiene un único firmante
    singleSigner: Boolean,
    // True cuando ya se ha recibido el evento de creación de la transacción
    transactionCreated: Boolean,
    testId: String
  },
  data() {
    return {
      reference: "",
      name: "",
      contact: "",
      confirmDialog: false,
      qrDialog: false,
    };
  },

  computed: {
    intervenerHasURL() {
      return Object.keys(this.intervener.metadata).some((key) => key === "SIGNATURE_CEREMONY#direct_link");
    },
    signatureUrl() {
      return this.intervener.metadata["SIGNATURE_CEREMONY#direct_link"];
    },
    // El QR inline sustituye al diálogo solo con un firmante y con el flag activo en el proceso.
    // faceToFaceHideConfirm tiene prioridad: ese flag abre el front renderer directamente.
    showInlineQr() {
      return (
        this.faceToFaceQrLink &&
        this.singleSigner &&
        !this.faceToFaceHideConfirm &&
        this.signCeremony &&
        this.signCeremonyEnabled &&
        this.intervenerHasURL
      );
    },
  },

  created() {
    if (this.type === "intervener") {
      this.reference = this.$vuetify.lang.t("$vuetify.notSpecified");
      this.name = this.$vuetify.lang.t("$vuetify.intervener");
      this.contact = this.$vuetify.lang.t("$vuetify.notSpecified");

      if (this.intervener.personal_info) {
        this.name = this.intervener.personal_info.name;
        if (
          this.intervener.personal_info.identification_document &&
          this.intervener.personal_info.identification_document.document_code !== ""
        ) {
          this.reference = this.intervener.personal_info.identification_document.document_code;
        }
      }

      if (this.intervener.contact_info) {
        if (this.intervener.contact_info.phone && this.intervener.contact_info.phone[0]) {
          this.contact =
            "+" + this.intervener.contact_info.phone[0].country_code + " " + this.intervener.contact_info.phone[0].phone_number;
        }
        //this.intervener.contact_info.email[0] ? this.intervener.contact_info.email[0] : this.intervener.contact_info.phone[0]
        if (this.intervener.contact_info.email && this.intervener.contact_info.email[0] !== "") {
          this.contact = this.intervener.contact_info.email[0];
        }
      }
    } else {
      this.reference = this.intervener.contact_id;
      this.name = this.intervener.contact_name + " " + this.intervener.contact_surname;
      if (this.intervener.contact_email !== "") {
        this.contact = this.intervener.contact_email;
      } else if (this.intervener.contact_phone !== "") {
        this.contact = this.intervener.contact_phone;
      } else {
        this.contact = this.$vuetify.lang.t("$vuetify.notSpecified");
      }
    }
  },
  methods: {
    getIntervenerColor,
    // Solo se invoca desde el botón "Ir a firma", que no se renderiza cuando showInlineQr es true
    handleSignatureClick() {
      if (this.faceToFaceHideConfirm) {
        this.openFrontRenderer();
      } else if (this.faceToFaceQrLink) {
        this.qrDialog = true;
      } else {
        this.confirmDialog = true;
      }
    },
    openFrontRenderer() {
      window.open(this.signatureUrl, "_blank");
      this.confirmDialog = false;
      this.qrDialog = false;
    },
  },
};
</script>

<style lang="scss">
.transaction-details-container {
  padding-left: 14px !important;
}

.transaction-parameters {
  border-left: 1px solid var(--card-border);

  span {
    font-size: 0.9rem;
  }
}

.intervener-transaction-card {
  border: solid 1px var(--card-border);
  min-height: 60px;
  align-content: space-evenly;
  border-radius: 4px;
  margin-bottom: 8px;

  .col {
    height: fit-content;
    margin: auto 0;
  }

  .v-btn {
  }

  .v-list-item__title,
  .v-list-item__subtitle {
    font-size: 0.9rem;
  }

  .icon {
    min-width: 50px !important;
  }

  .v-list-item__avatar {
    margin-right: 0 !important;
  }

  .v-list-item {
    padding: 0;

    .v-list-item__content {
      padding: 0;
      padding-left: 8px;
    }
  }

  .button-intervener-details {
    border: solid 1px var(--primary-alternative);
  }
}

// Recuadra el bloque del QR como una segunda fila dentro de la tarjeta del interviniente
.intervener-card-qr {
  border-top: 1px solid var(--card-border);
}

.col-intervener-text {
  display: block;
  white-space: normal;
  // overflow-wrap: break-word;
  // word-break: break-word;
  line-height: 1.2;
}


</style>
